import React, { useMemo, useState } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Button, TextField, Typography, Grid, LinearProgress, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useValoracionTable } from '../hooks/useValoracionTable';

const SORT_OPTIONS = [
  { value: 'fecha_desc', label: 'Más reciente', model: [{ field: 'fecha', sort: 'desc' }] },
  { value: 'pacienteId_asc', label: 'ID', model: [{ field: 'pacienteId', sort: 'asc' }] },
  { value: 'paciente_asc', label: 'Nombre', model: [{ field: 'paciente', sort: 'asc' }] },
];

function ValoracionFuncionalTable() {
  const navigate = useNavigate();
  const {
    loading,
    rows,
    filterPaciente,
    filterProfesional,
    setFilterPaciente,
    setFilterProfesional,
    fetchValoraciones,
  } = useValoracionTable();
  const [sortModel, setSortModel] = useState([{ field: 'fecha', sort: 'desc' }]);

  const currentSort = SORT_OPTIONS.find(
    o => o.model[0]?.field === sortModel[0]?.field && o.model[0]?.sort === sortModel[0]?.sort
  )?.value ?? 'fecha_desc';

  const columns = useMemo(
    () => [
      { field: 'pacienteId', headerName: 'ID', width: 90 },
      { field: 'paciente', headerName: 'Paciente', width: 200, flex: 1 },
      { field: 'profesional', headerName: 'Profesional', width: 220, flex: 1 },
      {
        field: 'fecha', headerName: 'Fecha', width: 140,
        sortComparator: (v1, v2, cp1, cp2) =>
          new Date(cp1.api.getRow(cp1.id).fechaRaw) - new Date(cp2.api.getRow(cp2.id).fechaRaw),
      },
      {
        field: 'actions',
        headerName: 'Acciones',
        width: 90,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <IconButton
            color="primary"
            onClick={() => navigate(`/ver-valoracion-funcional/${params.row.idTipo}/${params.row.pacienteId}`)}
          >
            <VisibilityIcon />
          </IconButton>
        ),
      },
    ],
    [navigate]
  );

  return (
    <>
      <Typography variant="h6" gutterBottom color="#0D6AEE">
        Informes de valoración funcional
      </Typography>

      <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} md={8} sx={{ display: 'flex', gap: 1, mb: { xs: 1.5, md: 0 } }}>
          <TextField
            label="Filtrar por paciente"
            value={filterPaciente}
            onChange={(e) => setFilterPaciente(e.target.value)}
            size="small"
          />
          <TextField
            label="Filtrar por profesional"
            value={filterProfesional}
            onChange={(e) => setFilterProfesional(e.target.value)}
            size="small"
          />
        </Grid>
        <Grid item>
          <ToggleButtonGroup
            value={currentSort}
            exclusive
            size="small"
            onChange={(_, val) => {
              const opt = SORT_OPTIONS.find(o => o.value === val);
              if (opt) setSortModel(opt.model);
            }}
          >
            {SORT_OPTIONS.map(o => <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>)}
          </ToggleButtonGroup>
        </Grid>
        <Grid item>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<RestartAltIcon />}
            onClick={() => {
              setFilterPaciente('');
              setFilterProfesional('');
              fetchValoraciones();
            }}
          >
            Resetear
          </Button>
        </Grid>
      </Grid>

      {loading ? (
        <LinearProgress />
      ) : (
        <div style={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={rows}
            columns={columns}
            pageSize={10}
            rowsPerPageOptions={[5, 10, 25, 50]}
            slots={{ toolbar: GridToolbar }}
            disableSelectionOnClick
            sortModel={sortModel}
            onSortModelChange={setSortModel}
          />
        </div>
      )}
    </>
  );
}

export default ValoracionFuncionalTable;
